import type { NetState } from './types'

/**
 * Reachability (doc §8): `navigator.onLine` lies (captive portals), so the
 * state combines it with a cheap heartbeat and recent request outcomes.
 * Spotty is a first-class state — Flaky keeps optimistic-local behavior on
 * while the sync engine keeps trying in the background.
 */

export interface ReachabilityOptions {
  /** Resolve true when the API answered (GET /api/v1/health, short timeout). */
  heartbeat: () => Promise<boolean>
  /** Probe cadence while degraded (ms). */
  intervalMs?: number
  /** Recent-window failures (out of 5) that mark the link flaky. */
  flakyThreshold?: number
}

export class ReachabilityMonitor {
  private opts: Required<ReachabilityOptions>
  private current: NetState = 'online'
  private listeners = new Set<(state: NetState) => void>()
  private recent: boolean[] = []
  private timer: ReturnType<typeof setInterval> | undefined
  private probing = false
  private onlineHandler = () => void this.probe()
  private offlineHandler = () => this.setState('offline')

  constructor(options: ReachabilityOptions) {
    this.opts = {
      heartbeat: options.heartbeat,
      intervalMs: options.intervalMs ?? 30_000,
      flakyThreshold: options.flakyThreshold ?? 2,
    }
  }

  get state(): NetState {
    return this.current
  }

  start(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.onlineHandler)
      window.addEventListener('offline', this.offlineHandler)
      if (!window.navigator.onLine) this.setState('offline')
    }
    // Probe every tick: navigator.onLine lies (doc §8) and a dead API
    // behind a live LAN must still flip the indicator without waiting for
    // a user action. The heartbeat is a tiny no-store GET.
    this.timer = setInterval(() => void this.probe(), this.opts.intervalMs)
  }

  stop(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.onlineHandler)
      window.removeEventListener('offline', this.offlineHandler)
    }
    if (this.timer) clearInterval(this.timer)
  }

  subscribe(listener: (state: NetState) => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  /** Feed real request outcomes into the window (doc §8 "success rate"). */
  noteSuccess(): void {
    this.push(true)
  }
  noteFailure(): void {
    this.push(false)
  }

  async probe(): Promise<NetState> {
    if (this.probing) return this.current
    this.probing = true
    try {
      const ok = await this.opts.heartbeat()
      this.push(ok)
    } catch {
      this.push(false)
    } finally {
      this.probing = false
    }
    return this.current
  }

  private push(ok: boolean): void {
    // Recovery from a hard outage is binary — one good heartbeat clears the
    // failure history so "Back online → Syncing…" is immediate (doc §14).
    if (ok && this.current === 'offline') this.recent = []
    this.recent.push(ok)
    if (this.recent.length > 5) this.recent.shift()
    const failures = this.recent.filter((r) => !r).length
    const browserOffline = typeof navigator !== 'undefined' && !navigator.onLine
    if (browserOffline || (!ok && failures >= this.recent.length && this.recent.length >= 2)) {
      this.setState('offline')
    } else if (failures >= this.opts.flakyThreshold) {
      this.setState('flaky')
    } else {
      this.setState('online')
    }
  }

  private setState(next: NetState): void {
    if (next === this.current) return
    this.current = next
    for (const listener of this.listeners) listener(next)
  }
}
